import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
    ExternalDropZone,
    Upload,
    UploadFileInfo,
    UploadFileStatus,
    UploadHttpHeaders,
    UploadOnAddEvent,
    UploadOnProgressEvent,
    UploadOnRemoveEvent,
    UploadOnStatusChangeEvent,
    UploadProps,
} from '@progress/kendo-react-upload';
import cn from 'clsx';
import styles from './style.module.scss';
import Button from '@components-clean/core/button';
import { useAuthorization, useLocalState } from '@hooks/index';
import NotificationService from '@utils/notification';

type Props = {
    saveUrl: UploadProps['saveUrl'];
    restrictions: UploadProps['restrictions'];
    onClose: (filesUploaded: boolean) => void;
    multiple?: boolean;
    title?: string;
    label?: string;
    maxFilesBeforeScroll?: number;
    customHint?: string;
};

const SELECTED_FILE_HEIGHT = 51;
const INITIAL_HEIGHT = 265;

// Using an empty fragment, as setting customNote on the component to null, false or undefined
// does not prevent the component from rendering its default note text...
// eslint-disable-next-line react/jsx-no-useless-fragment
const esLintSuppressedCustomNote = <></>;

/*
 https://www.telerik.com/kendo-react-ui/components/changelogs/ui-for-react#v14.4.1
 Includes bug fixes for multiple files uploading simultaneously where the UI doesn't refresh correctly

 The current version of the upload component is buggy and doesn't fully support a controlled mode of operation
 This results in some weird behaviour and odd workarounds that can hopefully be removed during the next Tech Debt cycle

 We need a weird combination of setting defaultFiles so that the UI updates accordingly, but the component doesn't seem
 to care about our file state prop...
 Thanks kendo.

 Things to test after tech debt tidy up:
 1. Upload button corectly disabled (can remove our uploadState and rely on file state)
 2. File progress UI refreshses correctly after removing defaultFiles and uploading state
 */
export const FileUploadDialog = ({
    saveUrl,
    restrictions,
    onClose,
    multiple,
    title,
    label,
    maxFilesBeforeScroll = 3,
    customHint,
}: Props) => {
    const user = useAuthorization();
    const { state } = useLocalState();
    const { t } = useTranslation();

    const uploadRef = useRef<Upload>(null);
    const [files, setFiles] = useState<UploadFileInfo[]>([]);
    // Used to force re-rendering when in controlled mode
    const [uploading, setUploading] = useState<boolean>(false);
    const filesUploaded = useRef<boolean>(false);

    let headers: UploadHttpHeaders | undefined;
    if (typeof saveUrl === 'string') {
        headers = {};
        if (user?.token) headers.Authorization = `Bearer ${user.token}`;
        if (state?.selectedOrganisation?.apiKey) headers.APIKey = state?.selectedOrganisation?.apiKey;
    }

    const onAdd = (event: UploadOnAddEvent) => {
        setFiles(event.newState);
    };

    const onRemove = (event: UploadOnRemoveEvent) => {
        setFiles(event.newState);
        setUploading(event.newState.some((f) => f.status === UploadFileStatus.Uploading));
    };

    const onProgress = (e: UploadOnProgressEvent) => {
        setFiles((prev) => {
            e.affectedFiles.forEach((f) => {
                const index = prev.findIndex((x) => x.uid === f.uid);
                if (index > -1) {
                    prev[index] = f;
                }
            });
            return prev;
        });
    };

    const onStatusChange = (e: UploadOnStatusChangeEvent) => {
        setFiles((prev) => {
            e.affectedFiles.forEach((f) => {
                const index = prev.findIndex((x) => x.uid === f.uid);
                if (index > -1) {
                    prev[index] = f;
                }
                if (f.status === UploadFileStatus.Uploaded) filesUploaded.current = true;
            });
            return prev;
        });

        const allUploaded = e.newState.length > 0 && e.newState.every((f) => f.status === UploadFileStatus.Uploaded);
        if (allUploaded) {
            onClose(filesUploaded.current);
            NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
        } else if (e.newState.length > 0 && !e.newState.some((f) => f.status === UploadFileStatus.Uploading))
            setUploading(false);
    };

    const getValidFilesForUpload = () => {
        return files.filter(
            (f) =>
                (f.status === UploadFileStatus.Initial ||
                    f.status === UploadFileStatus.UploadFailed ||
                    f.status === UploadFileStatus.Selected) &&
                (f.validationErrors == null || f.validationErrors?.indexOf('invalidFileExtension') === -1),
        );
    };

    const handleSubmit = () => {
        const valid = getValidFilesForUpload();
        if (valid.length === 0) return;

        const grouped: Record<string, UploadFileInfo[]> = {};
        valid.forEach((f) => {
            grouped[f.uid] = [f];
        });

        uploadRef.current?.uploadFiles(grouped);
        setUploading(true);
    };

    const calcDialogHeight = () => {
        const displayFilesCount = Math.min(files.length, maxFilesBeforeScroll);
        let extraPadding = 0;
        if (displayFilesCount === maxFilesBeforeScroll) extraPadding = 10;

        return INITIAL_HEIGHT + displayFilesCount * SELECTED_FILE_HEIGHT + extraPadding;
    };

    // The onDropCapture event (not on Drop) fires during the capture phase, and goes top down
    // therefore the ancestor can intercept the event before the Kendo External Drop Zone
    // allowing use to validate it and prevent the user from dropping multiple files in 1 go if we don't want them to
    const handleDropCapture = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            if (!multiple && event.dataTransfer?.files?.length > 1) {
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [multiple],
    );

    return (
        <Dialog
            title={title}
            height={calcDialogHeight()}
            width={647}
            onClose={() => onClose(filesUploaded.current)}
            className="upload-dialog"
        >
            <div className={styles.wrapper} onDropCapture={handleDropCapture}>
                <div>
                    {label && <div className="k-mb-1">{label}</div>}
                    <ExternalDropZone
                        className={cn(styles.dropzone, { [styles.dropzoneLabel]: !!label })}
                        uploadRef={uploadRef}
                        customNote={esLintSuppressedCustomNote}
                        customHint={customHint}
                    />
                </div>
                <Upload
                    ref={uploadRef}
                    files={files}
                    className={cn('k-w-full has-external-dropzone')}
                    defaultFiles={files} // Can be removed after tech debt?
                    withCredentials={false}
                    saveUrl={saveUrl}
                    restrictions={restrictions}
                    autoUpload={false}
                    saveHeaders={headers}
                    showActionButtons={false}
                    multiple={multiple}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    onProgress={onProgress}
                    onStatusChange={onStatusChange}
                />
            </div>
            <DialogActionsBar layout="end">
                <Button data-testid="cancel" onClick={() => onClose(filesUploaded.current)}>
                    {t('cancel')}
                </Button>
                <Button
                    data-testid="primary-upload"
                    primary
                    onClick={handleSubmit}
                    disabled={getValidFilesForUpload().length === 0 || uploading}
                >
                    {t('upload')}
                </Button>
            </DialogActionsBar>
        </Dialog>
    );
};
